// Receipts — powered, read-only operator surface.
//
// Loads api.commandCenter.receipts({ limit: 100 }) and renders the
// hash-chained Olivium receipt stream. Admin-gated (minLevel 80) and
// fails closed server-side; this view handles the three non-happy
// states honestly: loading, auth-required, and forbidden.
//
// By construction these receipts carry no secret material — chain_hash
// is a public integrity anchor, never a credential. Nothing here reveals
// a secret, so there is no reveal/copy-once flow on this surface.

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  DataGrid,
  Banner,
  InlineAlert,
  EmptyState,
  SeverityBadge,
  HashDisplay,
} from '@square/ui'
import { api, ApiError, isAuthError, isForbiddenError } from '../../../api/client'
import type { CommandCenterReceipt } from '../../../api/client'
import RouteHeader from '../../RouteHeader'

function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd} ${hh}:${mi}`
}

function truncateId(id: string | null): string {
  if (!id) return '—'
  return id.length > 12 ? `${id.slice(0, 12)}…` : id
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (!err.message || err.message === err.code) return err.code
    return `${err.code}: ${err.message}`
  }
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

export default function Receipts() {
  const [rows, setRows] = useState<CommandCenterReceipt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  const fetchReceipts = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)
    setForbidden(false)
    try {
      const result = await api.commandCenter.receipts({ limit: 100 })
      setRows(result.receipts || [])
    } catch (err) {
      if (isAuthError(err)) {
        setAuthRequired(true)
        setRows([])
        return
      }
      if (isForbiddenError(err) || (err instanceof ApiError && err.code === 'forbidden')) {
        setForbidden(true)
        setRows([])
        return
      }
      setError(formatError(err))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchReceipts() }, [fetchReceipts])

  return (
    <div>
      <RouteHeader
        kicker="OLIVIUM · RECEIPTS"
        title="Receipts"
        description="Hash-chained Olivium receipt stream — the append-only record of privileged actions."
        crumbs={[
          { label: 'Command Center', href: '/command-center/overview' },
          { label: 'Receipts' },
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={() => void fetchReceipts()}
            loading={loading}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      <div
        style={{
          fontSize: 12,
          color: 'var(--olv-text-secondary)',
          fontFamily: 'var(--font-body)',
          marginBottom: 16,
        }}
      >
        Hash-chained Olivium receipts. They carry no secret material by construction.
      </div>

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="warning" title="Receipts">{error}</Banner>
        </div>
      )}

      {authRequired ? (
        <EmptyState message="Sign in to continue — receipts require an authenticated operator session." />
      ) : forbidden ? (
        <EmptyState message="Operator access required — this is an admin-gated surface." />
      ) : loading && rows.length === 0 ? (
        <InlineAlert severity="info">Loading receipts…</InlineAlert>
      ) : (
        <DataGrid
          columns={[
            {
              id: 'created_at',
              header: 'WHEN',
              mono: true,
              width: 160,
              render: (r: CommandCenterReceipt) => formatStamp(r.created_at),
            },
            { id: 'action', header: 'ACTION' },
            { id: 'resource_type', header: 'RESOURCE', width: 150 },
            {
              id: 'resource_id',
              header: 'RESOURCE ID',
              mono: true,
              width: 140,
              render: (r: CommandCenterReceipt) => truncateId(r.resource_id),
            },
            {
              id: 'actor_role',
              header: 'ACTOR ROLE',
              mono: true,
              width: 130,
              render: (r: CommandCenterReceipt) => r.actor_role || '—',
            },
            {
              id: 'severity',
              header: 'SEVERITY',
              mono: true,
              width: 100,
              render: (r: CommandCenterReceipt) => r.severity || '—',
            },
            {
              id: 'outcome',
              header: 'OUTCOME',
              width: 110,
              render: (r: CommandCenterReceipt) => <SeverityBadge outcome={r.outcome} />,
            },
            {
              id: 'chain_hash',
              header: 'CHAIN HASH',
              width: 170,
              render: (r: CommandCenterReceipt) =>
                r.chain_hash ? <HashDisplay hash={r.chain_hash} /> : <span style={{ color: 'var(--olv-text-tertiary)' }}>—</span>,
            },
          ]}
          rows={rows}
          getRowId={(r: CommandCenterReceipt) => r.id}
          emptyMessage="No receipts recorded yet."
        />
      )}
    </div>
  )
}
